import * as React from "react";
import { Box, Typography, CircularProgress, Alert } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { fetchPatientOrders, fetchPatientReportLink } from "../../Services/apiService";
import OrderCard from "../../components/portal/OrderCard";
import StatusFilterChips from "../../components/portal/StatusFilterChips";
import PortalPagination from "../../components/portal/PortalPagination";
import { C } from "../../theme/dashboardCardTokens";
import { useTranslation } from "../../i18n/LanguageContext";

// Everything needed to render expandable order/test cards. Supports the
// backend's progressStatus filter (status chips above the list) and
// page/limit pagination.
function PatientOrders() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [orders, setOrders] = React.useState([]);
  const [pagination, setPagination] = React.useState(null);
  const [page, setPage] = React.useState(1);
  const [progressStatus, setProgressStatus] = React.useState("all");
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");

  const handleFilterChange = (status) => {
    setProgressStatus(status);
    setPage(1);
  };

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchPatientOrders(page, 20, progressStatus)
      .then((res) => {
        if (cancelled) return;
        setOrders(res.data?.orders || []);
        setPagination(res.data?.pagination || null);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err.status === 403) {
          navigate("/dashboard", { replace: true });
          return;
        }
        console.error("fetchPatientOrders failed:", { status: err.status, message: err.message, err });
        setError(err.message || t("portal.orders.patient.loadError"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, progressStatus, navigate]);

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
        {t("portal.orders.patient.title")}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t("portal.orders.patient.subtitle")}
      </Typography>

      <StatusFilterChips value={progressStatus} onChange={handleFilterChange} />

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {!loading && error && <Alert severity="error">{error}</Alert>}

      {!loading && !error && orders.length === 0 && (
        <Box sx={{ p: 4, textAlign: "center", color: C.textSecondary }}>
          {progressStatus === "all" ? t("portal.orders.patient.emptyAll") : t("portal.orders.patient.emptyFiltered")}
        </Box>
      )}

      {!loading &&
        !error &&
        orders.map((order, index) => (
          <OrderCard
            key={order.order_id}
            order={order}
            role="patient"
            getReportLink={fetchPatientReportLink}
            defaultExpanded={index === 0}
          />
        ))}

      {!loading && !error && <PortalPagination pagination={pagination} onChange={setPage} />}
    </Box>
  );
}

export default PatientOrders;
